import { useQuery } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { useEffect } from 'react';
import { RpmStatuses } from 'infrastructure/enums';

import { showResult } from '../../utils';
import { apiUrlGenerator } from '../../use-api';

import type {
  IRpmOnboardingStatusReportFilters,
  IRpmOnboardingStatusResponse,
  IRpmOnboardingStatusResponseData,
  IRpmPatientsByStatus,
} from 'infrastructure/interfaces';

const url = `reports/reports/onboarding-status`;

interface IFilterData
  extends Partial<
    Omit<
      IRpmOnboardingStatusReportFilters,
      'clinicGuid' | 'patientGuid' | 'doctorGuid'
    >
  > {
  clinicGuid?: string;
  doctorGuid?: string;
  guid?: string;
}

const getCountByStatus = (
  patientsByStatus: IRpmPatientsByStatus[],
  statusFind: RpmStatuses,
) => {
  if (statusFind === 'patient_refused') {
    return (
      patientsByStatus
        .filter((el) => el.status.includes('patient_refused'))
        .reduce((acum, current) => acum + current.count, 0) || 0
    );
  }
  const obj = patientsByStatus.find(({ status }) => status === statusFind);
  return obj ? obj.count : 0;
};

const prepareStatuses = ({
  patientsByStatus,
  activePatients,
  totalPatients,
}: IRpmOnboardingStatusResponseData) => {
  return {
    patient_referral: getCountByStatus(
      patientsByStatus,
      RpmStatuses.PatientReferral,
    ),
    referral_on_hold: getCountByStatus(
      patientsByStatus,
      RpmStatuses.ReferralOnHold,
    ),
    referral_withdrawn: getCountByStatus(
      patientsByStatus,
      RpmStatuses.ReferralWithdrawn,
    ),
    calling_queue: getCountByStatus(patientsByStatus, RpmStatuses.CallingQueue),
    patient_no_contact: getCountByStatus(
      patientsByStatus,
      RpmStatuses.PatientNoContact,
    ),
    refused: getCountByStatus(patientsByStatus, RpmStatuses.PatientRefused),
    insurance_refused: getCountByStatus(
      patientsByStatus,
      RpmStatuses.InsuranceRefused,
    ),
    onboarding: getCountByStatus(patientsByStatus, RpmStatuses.Onboarding),
    onboarding_unsuccessful: getCountByStatus(
      patientsByStatus,
      RpmStatuses.OnboardingUnsuccessful,
    ),
    total: totalPatients,
    active: activePatients,
  };
};

const queryFn = async (filter: IFilterData) => {
  const { data, error } =
    await dataService.getList<IRpmOnboardingStatusResponseData>(
      apiUrlGenerator(url, filter),
    );

  if (error) {
    return Promise.reject(error);
  }

  if (data) {
    return {
      statuses: prepareStatuses(data),
      insurances: data.patientsByInsurances,
    };
  }

  return data;
};

const getOnboardingStatusKey = (filter: IFilterData) => [
  'getRpmOnboardingStatus',
  filter,
];

export const useOnboardingStatus = (
  props: IRpmOnboardingStatusReportFilters,
) => {
  const filter: IFilterData = {
    childClinic: props.clinicGuid?.length ? props.childClinic : undefined,
    clinicGuid: props.clinicGuid?.join(','),
    doctorGuid: props.doctorGuid?.join(','),
  };

  const { data, error, isLoading, isFetching } = useQuery<
    IRpmOnboardingStatusResponse | undefined,
    string | undefined
  >({
    queryKey: getOnboardingStatusKey(filter),
    queryFn: async () => queryFn(filter),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (error) {
      showResult(error);
    }
  }, [error]);

  return { data, isLoading: isLoading || isFetching };
};
